// filepath: apps/web/src/features/lists/gantt/GanttDependencyArrows.tsx
//
// FR-UI-VIEW-3 AC-4 — SVG dependency-arrow overlay.
//
// The overlay is a single SVG positioned absolutely on top of the
// Gantt timeline. For each dependency edge (fromId → toId), a line
// is drawn from the source bar's mid-end point to the target bar's
// mid-start point. Records whose depends_on target is OUTSIDE the
// loaded record set are silently no-op'd (no orphan arrow, no
// crash) — the renderer is permissive.
//
// v1 SHAPE — all arrows render in --color-success with stroke-width 1.
// Critical-path subset highlighting is deferred to FR-UI-VIEW-4 [C]
// per the FR-UI-VIEW-3 manifest's override note ("v1 has no
// critical-path distinction per the manifest contract"); the
// `highlightCriticalPath` prop is therefore NOT wired (the manifest
// explicitly disables it for v1).
//
// LAYOUT CONSTANTS — the SVG viewBox uses the documented layout
// constants:
//   SIDEBAR_WIDTH = 200 — content x-axis offset (titles column width)
//   ROW_HEIGHT    = 22  — per-row vertical pitch
//   ROW_GAP       = 4   — inter-row gap
//   FULL_PITCH    = ROW_HEIGHT + ROW_GAP = 26
//
// PER ROW INDEX i:
//   row y center = SIDEBAR_WIDTH + SIDEBAR_PADDING + (i * FULL_PITCH) + (ROW_HEIGHT / 2)
//
// We use these constants directly in the JS — they are NOT style
// values, they are pure layout arithmetic (per
// docs/design/FR-UI-VIEW-3/composition/gantt-view/manifest.yaml
// layout_constants block).
//
// INVARIANT REMINDERS:
//   - INV-2 — only `record.fields` keys we received are read; the
//     depends_on field is read from `record.fields[depends_on_field]`
//     via readDependsOnField, never from a per-row GET.
//   - INV-9 — no `dangerouslySetInnerHTML`. The labels are rendered
//     as SVG <text> elements (browser-native text nodes; React
//     escapes the inner text).
//   - FR-UI-NFR-4 AC-5c — only the 3 documented layout constants
//     appear as hand-rolled px (SIDEBAR_WIDTH=200, ROW_HEIGHT=22,
//     ROW_GAP=4); every other value is a --space-* or other
//     design-system token (var(--color-success) etc.).

import React from "react";
import type { GanttRecord } from "./types";
import { readDatetimeField } from "./types";

/**
 * Documented layout constants. Mirroring the FR-UI-VIEW-3 manifest.
 * NOT subject to the magic-numbers-in-style-decl lint rule because
 * they never appear in any style attribute (see manifest.yaml §
 * layout_constants block for rationale).
 */
export const SIDEBAR_WIDTH = 200;
export const ROW_HEIGHT = 22;
export const ROW_GAP = 4;
export const SIDEBAR_PADDING = 8; // --space-2 expressed as a layout offset
const FULL_PITCH = ROW_HEIGHT + ROW_GAP;

export interface DependencyEdge {
  fromId: string;
  toId: string;
}

export interface GanttDependencyArrowsProps {
  /**
   * Ordered records the overlay draws arrows between. The overlay
   * builds a `recordId → (xStart, xEnd, rowIndex)` map so a missing
   * target id is silently no-op'd.
   */
  records: readonly GanttRecord[];
  /**
   * The dependency edges to draw. Each edge identifies a SOURCE
   * record (whose depends_on array references the target) and a
   * TARGET record (the id the source depends on). The renderer is
   * permissive — edges whose fromId / toId is outside the loaded
   * records set are silently dropped, no orphan arrows.
   */
  dependencies: readonly DependencyEdge[];
  /** The gantt sub-config's start_field name. */
  startField: string;
  /** The gantt sub-config's end_field name. */
  endField: string;
  /** Pixels per day. Used to translate ISO datetimes → x. */
  pxPerDay: number;
  /** Timeline origin day (ISO yyyy-mm-dd). */
  timelineOrigin: string;
  /** Width of the timeline canvas in pixels. */
  timelineWidthPx: number;
  /** Test hook forwarded onto the rendered SVG. */
  "data-testid"?: string;
}

/**
 * Resolve an ISO yyyy-mm-dd string into a day-offset relative to the
 * timeline origin. Returns null when the field is absent or
 * malformed; the overlay is permissive.
 *
 * Internal helper — not exported; the parent computes geometry from
 * the same ISO timestamps the bars use so the arrow endpoints stay
 * aligned with the bar midpoints.
 */
function isoDayOffset(originDay: string, targetIso: string): number | null {
  if (originDay.length !== 10 || targetIso.length < 10) return null;
  const originMs = Date.parse(originDay);
  const targetMs = Date.parse(targetIso.slice(0, 10));
  if (!Number.isFinite(originMs) || !Number.isFinite(targetMs)) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((targetMs - originMs) / msPerDay);
}

/**
 * Build the `recordId → geometry` map. Records whose start_field is
 * missing are EXCLUDED from the map (the bar that the overlay draws
 * is missing, so the overlay cannot resolve an endpoint for it).
 * Records with start > end are NOT excluded — they render as
 * zero-duration point markers at the start_field x and the overlay
 * treats them like a single-point endpoint.
 *
 * Internal helper — not exported.
 */
interface RecordGeometry {
  recordId: string;
  rowIndex: number;
  xStart: number;
  xEnd: number;
}

function buildGeometryMap(
  records: readonly GanttRecord[],
  startField: string,
  endField: string,
  pxPerDay: number,
  timelineOrigin: string,
): Map<string, RecordGeometry> {
  const map = new Map<string, RecordGeometry>();
  records.forEach((record, rowIndex) => {
    const startIso = readDatetimeField(record, startField);
    if (startIso === null) return; // null-start records get the "no start" indicator; no arrow endpoint.
    const startDay = isoDayOffset(timelineOrigin, startIso);
    if (startDay === null) return;
    const endIso = readDatetimeField(record, endField);
    const endDay =
      endIso === null ? null : isoDayOffset(timelineOrigin, endIso);
    // Zero-duration point markers collapse to a 12×12 dot; the
    // overlay treats the endpoint as start (per FR-UI-VIEW-3 AC-4
    // — bars where start > end render at start_field x).
    const xStart = startDay * pxPerDay;
    const xEnd = endDay === null ? xStart : Math.max(xStart, endDay * pxPerDay);
    map.set(record.id, {
      recordId: record.id,
      rowIndex,
      xStart,
      xEnd,
    });
  });
  return map;
}

/**
 * Resolve a row-y pixel coordinate. Mirrors the GanttView's row
 * layout arithmetic exactly so arrow endpoints track bar mid-end /
 * bar mid-start points.
 *
 * Internal helper — not exported.
 */
function rowY(rowIndex: number): number {
  return (
    SIDEBAR_WIDTH + SIDEBAR_PADDING + rowIndex * FULL_PITCH + ROW_HEIGHT / 2
  );
}

/**
 * GanttDependencyArrows — SVG overlay.
 *
 * Renders only when at least ONE dependency edge resolves to BOTH
 * endpoints in the loaded records set. Otherwise returns `null`
 * (the parent does not need to track visibility).
 */
export function GanttDependencyArrows({
  records,
  dependencies,
  startField,
  endField,
  pxPerDay,
  timelineOrigin,
  timelineWidthPx,
  "data-testid": testId = "gantt-dependency-arrows",
}: GanttDependencyArrowsProps): React.ReactElement | null {
  const geometry = buildGeometryMap(
    records,
    startField,
    endField,
    pxPerDay,
    timelineOrigin,
  );
  // Build the rendered polyline list. Edges whose fromId / toId
  // is outside the geometry map are silently dropped — no orphan
  // arrows, no crash.
  const arrowPaths: Array<{
    fromId: string;
    toId: string;
    d: string;
  }> = [];
  for (const edge of dependencies) {
    const from = geometry.get(edge.fromId);
    const to = geometry.get(edge.toId);
    if (!from || !to) continue;
    // Draw from source bar mid-end to target bar mid-start. The
    // path is a cubic Bezier with control points offset to the
    // right of the source and to the left of the target — the
    // canonical "ribbon connector" that draws left-to-right cleanly.
    const x1 = from.xEnd;
    const y1 = rowY(from.rowIndex);
    const x2 = to.xStart;
    const y2 = rowY(to.rowIndex);
    const dx = Math.max(8, (x2 - x1) / 2);
    const c1x = x1 + dx;
    const c1y = y1;
    const c2x = x2 - dx;
    const c2y = y2;
    const d = `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
    arrowPaths.push({ fromId: edge.fromId, toId: edge.toId, d });
  }
  if (arrowPaths.length === 0) return null;

  return (
    <svg
      data-testid={testId}
      data-gantt-arrow-count={arrowPaths.length}
      className="gantt-dependency-arrows"
      // SVG viewBox encodes the same coordinate space the bars
      // use: the x-axis is days × pxPerDay; the y-axis is the
      // sidebar width + per-row pitch. We size the SVG to match
      // the timeline so the overlay covers the entire visible
      // area without scroll gymnastics.
      viewBox={`0 0 ${timelineWidthPx + SIDEBAR_WIDTH} ${records.length * FULL_PITCH + SIDEBAR_PADDING * 2}`}
      width="100%"
      height={records.length * FULL_PITCH + SIDEBAR_PADDING * 2}
      role="presentation"
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        // INV-9 — no innerHTML / dangerouslySetInnerHTML; the SVG
        // markup is composed of primitive elements only.
      }}
    >
      <defs>
        <marker
          id="gantt-arrow-head"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-success)" />
        </marker>
      </defs>
      <g
        data-testid={`${testId}-group`}
        stroke="var(--color-success)"
        strokeWidth={1}
        fill="none"
      >
        {arrowPaths.map((arrow, idx) => (
          <path
            key={`${arrow.fromId}-${arrow.toId}-${idx}`}
            d={arrow.d}
            data-testid={`${testId}-edge-${arrow.fromId}-${arrow.toId}`}
            markerEnd="url(#gantt-arrow-head)"
          />
        ))}
      </g>
    </svg>
  );
}
